import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, MapPin, Layers } from "lucide-react";

const WINDOWS = [
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "all", label: "All time" },
];

export default function TrendingReport() {
    const { user } = useAuth();
    const isDM = user?.role === "DISTRICT_MANAGER";
    const [tab, setTab] = useState("POST");
    const [win, setWin] = useState("7d");
    const [districts, setDistricts] = useState([]);
    const [district, setDistrict] = useState(isDM ? (user?.district || "") : "__all__");
    const [breakdown, setBreakdown] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("target_type", tab);
        params.set("window", win);
        params.set("limit", "20");
        if (breakdown && !isDM) params.set("breakdown", "by_district");
        else if (district && district !== "__all__") params.set("district_slug", district);
        api.get(`/admin/trending?${params}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(errorMessage(e)))
            .finally(() => setLoading(false));
    }, [tab, win, district, breakdown, isDM]);

    return (
        <div data-testid="trending-report">
            <div className="mb-4">
                <div className="text-sm font-medium text-orange-600">Reports</div>
                <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 flex items-center gap-2"><TrendingUp /> Trending</h1>
                <p className="mt-1 text-sm text-neutral-500">Top posts and shops by favorite count. Filter by window and district.</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="trend-target-tabs">
                        <TabsTrigger value="POST" data-testid="trend-tab-posts">Posts</TabsTrigger>
                        <TabsTrigger value="SHOP" data-testid="trend-tab-shops">Shops</TabsTrigger>
                    </TabsList>
                </Tabs>
                <Tabs value={win} onValueChange={setWin}>
                    <TabsList data-testid="trend-window-tabs">
                        {WINDOWS.map((w) => <TabsTrigger key={w.key} value={w.key} data-testid={`trend-win-${w.key}`}>{w.label}</TabsTrigger>)}
                    </TabsList>
                </Tabs>
                {!isDM && (
                    <>
                        <Select value={district} onValueChange={setDistrict} disabled={breakdown}>
                            <SelectTrigger className="w-52" data-testid="trend-district"><SelectValue placeholder="All districts" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All districts</SelectItem>
                                {districts.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button variant={breakdown ? "default" : "outline"} onClick={() => setBreakdown((b) => !b)} data-testid="trend-breakdown-toggle">
                            <Layers size={14} className="mr-2" /> {breakdown ? "By district ✓" : "By district"}
                        </Button>
                    </>
                )}
                {isDM && (
                    <Badge variant="outline" className="text-xs"><MapPin size={12} className="mr-1" /> Locked to {user.district}</Badge>
                )}
            </div>

            <div className="mt-4">
                {loading ? (
                    <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
                ) : breakdown && !isDM ? (
                    <BreakdownView data={data} target={tab} />
                ) : (
                    <SingleView items={data?.items || []} target={tab} />
                )}
            </div>
        </div>
    );
}

function SingleView({ items, target }) {
    if (!items.length) return <Card className="p-8 text-center text-sm text-neutral-500">No trending items in this window.</Card>;
    return (
        <Card className="overflow-hidden">
            <Table>
                <TableHeader><TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{target === "POST" ? "Post" : "Shop"}</TableHead>
                    {target === "POST" && <TableHead>Business</TableHead>}
                    <TableHead>District · Category</TableHead>
                    <TableHead className="text-right">Favorites</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                    {items.map((it, i) => (
                        <TableRow key={it.target_id} data-testid={`trend-row-${it.target_id}`}>
                            <TableCell className="text-xs text-neutral-500">{i + 1}</TableCell>
                            <TableCell className="max-w-[280px]">
                                <div className="flex items-center gap-2">
                                    {target === "POST" && it.poster_image_url && (
                                        <img src={publicAssetUrl(it.poster_image_url)} alt="" className="h-8 w-8 rounded object-cover" />
                                    )}
                                    <div className="truncate text-sm font-semibold">{it.title || it.business_name}</div>
                                </div>
                            </TableCell>
                            {target === "POST" && <TableCell className="text-xs">{it.business_name}</TableCell>}
                            <TableCell className="text-xs text-neutral-500">{(it.district_slug || it.business_district) + " · " + (it.category_slug || it.business_category || "—")}</TableCell>
                            <TableCell className="text-right font-mono font-bold">{it.favorite_count_in_window}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

function BreakdownView({ data, target }) {
    const list = data?.by_district || [];
    if (!list.length) return <Card className="p-8 text-center text-sm text-neutral-500">No favorites in this window.</Card>;
    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="trend-breakdown-grid">
            {list.map((d) => (
                <Card key={d.district_slug} className="p-4" data-testid={`trend-district-${d.district_slug}`}>
                    <div className="mb-2 flex items-center justify-between">
                        <div className="text-sm font-bold text-neutral-900"><MapPin size={12} className="mr-1 inline" /> {d.district_slug}</div>
                        <Badge className="bg-orange-500 text-white">{d.total_favorites}</Badge>
                    </div>
                    <ul className="space-y-1">
                        {(d.top_items || []).slice(0, 3).map((it, i) => (
                            <li key={it.target_id} className="flex items-center gap-2 text-xs">
                                <span className="text-neutral-400">{i + 1}.</span>
                                <span className="min-w-0 flex-1 truncate">{it.title || it.business_name}</span>
                                <span className="font-mono font-bold text-orange-600">{it.favorite_count_in_window}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            ))}
        </div>
    );
}
